import { SearchIcon } from '@chakra-ui/icons'
import {
  Flex,
  Heading,
  Button,
  InputGroup,
  InputLeftElement,
  Input,
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Center,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import UserMenu from '../../../../app/components/UserMenu'
import { useGetDeviceGroupsQuery, useDeleteDeviceGroupMutation } from '../../api/api'
import { DeleteGroupModal } from './modals/DeleteGroupModal'
import { DeviceGroupModal } from './modals/DeviceGroupModal'
import { HeaderText, RowText, DeviceNamesCell, KebabMenu } from '../utils'

type ModalMode = 'create' | 'edit'
type EditGroup = { id: number; name: string; deviceIds: number[] } | null

export function DeviceGroups() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<ModalMode>('create')
  const [editGroup, setEditGroup] = useState<EditGroup>(null)

  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(
    null,
  )

  const [search, setSearch] = useState('')
  const { data, isLoading } = useGetDeviceGroupsQuery()
  const [deleteDeviceGroup] = useDeleteDeviceGroupMutation()

  const hasGroups = (data?.length ?? 0) > 0
  const filteredGroups = (data ?? []).filter((d: any) =>
    d.name.toLowerCase().includes(search.toLowerCase()),
  )

  const openCreate = () => {
    setModalMode('create')
    setEditGroup(null)
    setIsModalOpen(true)
  }

  const openEdit = (group: any) => {
    setModalMode('edit')
    setEditGroup({
      id: group.id,
      name: group.name,
      deviceIds: (group.devices ?? []).map((d: any) => d.id),
    })
    setIsModalOpen(true)
  }

  const openDelete = (group: any) => {
    setDeleteTarget({ id: group.id, name: group.name })
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteDeviceGroup(deleteTarget as any).unwrap()
      toast.success(`Group deleted successfully`, {
        style: { padding: '10px 20px' },
        isLoading: false,
        autoClose: 3000,
      })
    } catch (error: any) {
      toast.error(
        `${error?.data?.message ? error.data.message : 'Something went wrong!'}`,
        {
          style: { padding: '10px 20px' },
          isLoading: false,
          autoClose: 3000,
        },
      )
    }
    setDeleteTarget(null)
  }

  if (isLoading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" />
      </Center>
    )
  }

  return (
    <>
      <DeviceGroupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        initialGroup={modalMode === 'edit' && editGroup ? editGroup : undefined}
      />

      <DeleteGroupModal
        deleteTarget={deleteTarget}
        setDeleteTarget={setDeleteTarget}
        handleConfirmDelete={handleConfirmDelete}
      />

      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md">Control your groups</Heading>
        <Box display="flex" alignItems="center" gap={4}>
          <UserMenu />
        </Box>
      </Flex>

      {!hasGroups ? (
        <Center minH="60vh">
          <VStack spacing={4}>
            <Heading
              as="h3"
              fontFamily="Inter"
              fontWeight={600}
              fontSize="22px"
              color="#3F3F3F"
              textAlign="center"
            >
              Group devices to track activity
            </Heading>
            <Text
              fontFamily="Inter"
              fontWeight={400}
              fontSize="14px"
              color="#707070"
              textAlign="center"
              maxW="360px"
            >
              You’ll see your device groups here. No groups have been created yet.
            </Text>
            <Button colorScheme="blue" onClick={openCreate}>
              Create Group
            </Button>
          </VStack>
        </Center>
      ) : (
        <>
          <Flex gap={5} align="center" mb={6}>
            <InputGroup maxW="400px" flex="1">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                rounded="full"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
            <Button width="200px" colorScheme="blue" onClick={openCreate}>
              Create Group
            </Button>
          </Flex>

          {/* Table */}
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <HeaderText>Group</HeaderText>
                  <HeaderText>Device/s</HeaderText>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredGroups.map((group: any) => (
                  <Tr key={group.id}>
                    <Td>
                      <RowText>{group.name}</RowText>
                    </Td>
                    <Td>
                      <DeviceNamesCell devices={group.devices ?? []} />
                    </Td>
                    <Td textAlign="right">
                      <KebabMenu
                        onEdit={() => openEdit(group)}
                        onRemove={() => openDelete(group)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </>
  )
}
